import { StoredPacket } from '../storage/db';
import { getNodeColor, snrToOpacity } from './nodeColor';
import { calculateDistanceKm } from '../storage/fusionEngine';

/**
 * Proximity threshold in kilometers (10 meters) for spatial deduplication.
 * Successive packets received from the same node within this radius are merged
 * into a single coverage spot without alpha multiplication.
 */
export const COVERAGE_CLUSTER_RADIUS_KM = 0.010; // 10 meters

export interface CoverageSpotData {
  id: string;
  nodeId: number;
  nodeName: string;
  color: string;
  lat: number;
  lon: number;
  maxSnr: number;
  latestRssi: number;
  opacity: number;
  packetCount: number;
  lastHeard: number;
  isRelayed: boolean;
  packets: StoredPacket[];
}

export interface RecordResult {
  spot: CoverageSpotData;
  isNew: boolean;
}

/**
 * Manages spatial deduplication of LoRa coverage points to eliminate opacity stacking
 * during stationary reception while preserving natural color blending across distinct nodes.
 */
export class SpatialCoverageManager {
  private spotsByNode: Map<number, CoverageSpotData[]> = new Map();
  private allSpotsMap: Map<string, CoverageSpotData> = new Map();
  private nextSpotIndex = 1;

  /**
   * Records an incoming packet into the spatial coverage index.
   *
   * Why: Standard semi-transparent markers stack opacity when multiple packets arrive at the
   * exact same GPS coordinate from the same node ($1 - (1 - \alpha)^n \to 1$), falsely making
   * weak signals appear strong. This method aggregates packets from the same node within a 10m
   * radius into a single spot with non-additive $\max(\text{snr})$ opacity, while allowing
   * packets from different nodes to remain distinct overlapping markers.
   *
   * @param packet - Newly received LoRa packet with valid GPS coordinates
   * @param lastHopNodeId - Resolved transmitting node ID (relay or direct sender)
   * @param lastHopName - Human-readable name of transmitting node
   * @param isRelayed - True if packet arrived via intermediate relay
   * @returns RecordResult containing the active spot data and an isNew boolean flag
   */
  recordPacket(
    packet: StoredPacket,
    lastHopNodeId: number,
    lastHopName: string,
    isRelayed: boolean
  ): RecordResult {
    if (packet.lat === null || packet.lon === null) {
      throw new Error('Packet coordinates must not be null');
    }

    const lat = packet.lat;
    const lon = packet.lon;

    let nodeSpots = this.spotsByNode.get(lastHopNodeId);
    if (!nodeSpots) {
      nodeSpots = [];
      this.spotsByNode.set(lastHopNodeId, nodeSpots);
    }

    // Search for an existing spot from the SAME node within the cluster radius
    let existingSpot: CoverageSpotData | null = null;
    for (const spot of nodeSpots) {
      if (calculateDistanceKm(lat, lon, spot.lat, spot.lon) <= COVERAGE_CLUSTER_RADIUS_KM) {
        existingSpot = spot;
        break;
      }
    }

    if (existingSpot) {
      existingSpot.packetCount++;
      existingSpot.lastHeard = Math.max(existingSpot.lastHeard, packet.timestamp);
      existingSpot.latestRssi = packet.rssi;
      existingSpot.packets.push(packet);

      // Non-additive rule: update opacity only if current packet has a higher SNR
      if (packet.snr > existingSpot.maxSnr) {
        existingSpot.maxSnr = packet.snr;
        existingSpot.opacity = snrToOpacity(existingSpot.maxSnr);
      }

      return { spot: existingSpot, isNew: false };
    }

    // New coverage spot for this node
    const spotId = `spot_${lastHopNodeId}_${this.nextSpotIndex++}`;
    const color = getNodeColor(lastHopNodeId, lastHopName);
    const opacity = snrToOpacity(packet.snr);

    const newSpot: CoverageSpotData = {
      id: spotId,
      nodeId: lastHopNodeId,
      nodeName: lastHopName,
      color,
      lat,
      lon,
      maxSnr: packet.snr,
      latestRssi: packet.rssi,
      opacity,
      packetCount: 1,
      lastHeard: packet.timestamp,
      isRelayed,
      packets: [packet]
    };

    nodeSpots.push(newSpot);
    this.allSpotsMap.set(spotId, newSpot);

    return { spot: newSpot, isNew: true };
  }

  /**
   * Retrieves all registered coverage spot data objects.
   *
   * @returns Array of CoverageSpotData
   */
  getAllSpots(): CoverageSpotData[] {
    return Array.from(this.allSpotsMap.values());
  }

  /**
   * Clears all indexed spatial coverage spots.
   */
  clear(): void {
    this.spotsByNode.clear();
    this.allSpotsMap.clear();
    this.nextSpotIndex = 1;
  }
}
